import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Check, Move, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ImageCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onClose: () => void;
  onCrop: (croppedImage: string) => void;
  aspectRatio?: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  isOpen,
  imageSrc,
  onClose,
  onCrop,
  aspectRatio = 1, // Default to square (1:1)
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, size: 200 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, size: 0, cropX: 0, cropY: 0 });
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [cropDragStart, setCropDragStart] = useState({ x: 0, y: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load image and calculate initial crop area
  useEffect(() => {
    if (imageSrc && isOpen) {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height });
        setImageLoaded(true);
        
        // Reset positions
        setImagePosition({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        
        // Calculate initial crop area size (70% of container for better fit)
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const containerHeight = containerRef.current.clientHeight;
          const containerSize = Math.min(containerWidth, containerHeight);
          const cropSize = containerSize * 0.7;
          setCropArea({
            x: containerWidth / 2 - cropSize / 2,
            y: containerHeight / 2 - cropSize / 2,
            size: cropSize,
          });
        }
      };
      img.src = imageSrc;
    }
  }, [imageSrc, isOpen]);

  // Handle mouse drag for image movement
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Check if clicking on crop box or its handles
    if (target.closest('.crop-box') || target.closest('.crop-handle')) {
      return; // Don't drag image if clicking on crop box
    }
    
    if (e.target === imageRef.current || (imageRef.current && imageRef.current.contains(e.target as Node))) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y,
      });
    }
  }, [imagePosition]);

  // Handle crop box drag
  const handleCropBoxMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (containerRef.current) {
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      setIsDraggingCrop(true);
      setCropDragStart({
        x: e.clientX - rect.left - cropArea.x,
        y: e.clientY - rect.top - cropArea.y,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setImagePosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
      if (isDraggingCrop && containerRef.current) {
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const newX = e.clientX - rect.left - cropDragStart.x;
        const newY = e.clientY - rect.top - cropDragStart.y;
        
        // Constrain crop box within container bounds
        const maxX = container.clientWidth - cropArea.size;
        const maxY = container.clientHeight - cropArea.size;
        
        setCropArea(prev => ({
          ...prev,
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        }));
      }
      if (isResizing) {
        if (containerRef.current) {
          const container = containerRef.current;
          const rect = container.getBoundingClientRect();
          const centerX = rect.left + resizeStart.x + resizeStart.size / 2;
          const centerY = rect.top + resizeStart.y + resizeStart.size / 2;
          const distance = Math.sqrt(
            Math.pow(e.clientX - centerX, 2) +
            Math.pow(e.clientY - centerY, 2)
          );
          const newSize = Math.max(100, Math.min(Math.min(container.clientWidth, container.clientHeight) * 0.9, distance * 2));
          const sizeDelta = newSize - resizeStart.size;
          setCropArea(prev => ({
            ...prev,
            size: newSize,
            x: resizeStart.x - sizeDelta / 2,
            y: resizeStart.y - sizeDelta / 2,
          }));
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setIsDraggingCrop(false);
    };

    if (isDragging || isResizing || isDraggingCrop) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, isDraggingCrop, dragStart, cropDragStart, cropArea, resizeStart]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    if (containerRef.current) {
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      setResizeStart({
        x: cropArea.x,
        y: cropArea.y,
        size: cropArea.size,
        cropX: cropArea.x,
        cropY: cropArea.y,
      });
    }
  };

  const handleCrop = () => {
    if (!imageRef.current || !canvasRef.current || !containerRef.current) return;

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current!;
      const container = containerRef.current!;
      
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate displayed image dimensions
      const displayWidth = img.width * zoom;
      const displayHeight = img.height * zoom;

      // Calculate image position in container
      const imageX = (containerWidth - displayWidth) / 2 + imagePosition.x;
      const imageY = (containerHeight - displayHeight) / 2 + imagePosition.y;

      // Calculate crop box position relative to container
      const cropBoxX = cropArea.x;
      const cropBoxY = cropArea.y;
      const cropBoxSize = cropArea.size;

      // Calculate source coordinates in the original image
      const sourceX = ((cropBoxX - imageX) / displayWidth) * img.width;
      const sourceY = ((cropBoxY - imageY) / displayHeight) * img.height;
      const sourceSize = (cropBoxSize / displayWidth) * img.width;

      // Set canvas size
      const outputSize = 400;
      canvas.width = outputSize;
      canvas.height = outputSize;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Apply rotation if needed
      if (rotation !== 0) {
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);
      }

      // Draw cropped image
      ctx.drawImage(
        img,
        Math.max(0, sourceX),
        Math.max(0, sourceY),
        Math.min(sourceSize, img.width - Math.max(0, sourceX)),
        Math.min(sourceSize, img.height - Math.max(0, sourceY)),
        0,
        0,
        outputSize,
        outputSize
      );

      // Restore context if rotation was applied
      if (rotation !== 0) {
        ctx.restore();
      }

      // Get cropped image as data URL
      const croppedImage = canvas.toDataURL('image/png');
      onCrop(croppedImage);
      onClose();
    };
    img.src = imageSrc;
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden mx-4"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#F2F8FC] to-[#F2F8FC]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0084CF] rounded-full flex items-center justify-center">
                <Move className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Crop Your Photo</h2>
                <p className="text-xs text-gray-600">Drag to move, drag corners to resize</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Crop Area */}
          <div className="flex-1 p-4 overflow-auto bg-gray-900">
            <div
              ref={containerRef}
              className="relative w-full h-[350px] sm:h-[400px] bg-gray-900 rounded-lg overflow-hidden"
              onMouseDown={handleMouseDown}
            >
              {imageLoaded && (
                <>
                  {/* Image */}
                  <div
                    className="absolute inset-0"
                    style={{
                      transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                      transformOrigin: 'center center',
                      cursor: isDragging ? 'grabbing' : 'grab',
                    }}
                  >
                    <img
                      ref={imageRef}
                      src={imageSrc}
                      alt="Crop"
                      draggable={false}
                      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 select-none"
                      style={{
                        maxWidth: 'none',
                        maxHeight: 'none',
                        width: imageSize.width,
                        height: imageSize.height,
                      }}
                    />
                  </div>

                  {/* Crop Overlay - Gmail style */}
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Dark overlay */}
                    <svg className="absolute inset-0 w-full h-full">
                      <defs>
                        <mask id="crop-mask">
                          <rect width="100%" height="100%" fill="black" />
                          <rect
                            x={cropArea.x}
                            y={cropArea.y}
                            width={cropArea.size}
                            height={cropArea.size}
                            fill="white"
                            rx="4"
                          />
                        </mask>
                      </defs>
                      <rect
                        width="100%"
                        height="100%"
                        fill="rgba(0, 0, 0, 0.5)"
                        mask="url(#crop-mask)"
                      />
                    </svg>

                    {/* Crop box */}
                    <div
                      className="absolute border-2 border-white shadow-2xl"
                      style={{
                        left: `${cropArea.x}px`,
                        top: `${cropArea.y}px`,
                        width: `${cropArea.size}px`,
                        height: `${cropArea.size}px`,
                        borderRadius: '4px',
                        boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      {/* Corner handles - resizable */}
                      <div
                        className="absolute -top-1 -left-1 w-4 h-4 bg-white border-2 border-[#0084CF] rounded-sm cursor-nwse-resize hover:scale-125 hover:bg-[#F2F8FC] transition-transform crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div
                        className="absolute -top-1 -right-1 w-4 h-4 bg-white border-2 border-[#0084CF] rounded-sm cursor-nesw-resize hover:scale-125 hover:bg-[#F2F8FC] transition-transform crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div
                        className="absolute -bottom-1 -left-1 w-4 h-4 bg-white border-2 border-[#0084CF] rounded-sm cursor-nesw-resize hover:scale-125 hover:bg-[#F2F8FC] transition-transform crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div
                        className="absolute -bottom-1 -right-1 w-4 h-4 bg-white border-2 border-[#0084CF] rounded-sm cursor-nwse-resize hover:scale-125 hover:bg-[#F2F8FC] transition-transform crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      
                      {/* Edge handles for better resizing */}
                      <div
                        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-2 bg-white border border-[#0084CF] rounded cursor-ns-resize hover:bg-[#F2F8FC] crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-8 h-2 bg-white border border-[#0084CF] rounded cursor-ns-resize hover:bg-[#F2F8FC] crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-8 bg-white border border-[#0084CF] rounded cursor-ew-resize hover:bg-[#F2F8FC] crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                      <div
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2 h-8 bg-white border border-[#0084CF] rounded cursor-ew-resize hover:bg-[#F2F8FC] crop-handle z-10"
                        onMouseDown={handleResizeStart}
                        style={{ pointerEvents: 'auto' }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="space-y-3">
              {/* Zoom Control */}
              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#0066A3]"
                />
                <ZoomIn className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-700 w-10 text-right flex-shrink-0">
                  {Math.round(zoom * 100)}%
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={handleRotate}
                  className="flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 rounded-lg font-medium text-xs transition-all shadow-sm"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  Rotate
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 rounded-lg font-medium text-xs transition-all shadow-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCrop}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#0084CF] hover:bg-[#0066A3] text-white rounded-lg font-medium text-xs transition-all shadow-md"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Set as Profile Picture
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Hidden canvas for cropping */}
          <canvas ref={canvasRef} className="hidden" />
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ImageCropModal;
